// src/components/native-permissions-gate/native-permissions-gate.tsx
import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { isNative, isIos } from '../../platform';
import { openAppSettings } from '../../helpers/open-app-settings';
import { cacheNativeToken } from '../../platform/push';

type PermissionStatus = 'checking' | 'granted' | 'denied' | 'requesting';

type PermissionState = {
  microphone: PermissionStatus;
  notifications: PermissionStatus;
};

const INITIAL: PermissionState = {
  microphone: 'checking',
  notifications: 'checking',
};

/** Track whether getUserMedia succeeded so we don't rely on the Permissions API. */
let micGrantedViaStream = false;

async function checkMicrophone(): Promise<'granted' | 'denied' | 'prompt'> {
  // If getUserMedia already succeeded, trust that over the Permissions API —
  // Android WebView often returns 'prompt' even after the user taps "Allow".
  if (micGrantedViaStream) return 'granted';

  try {
    const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    if (result.state === 'granted') return 'granted';
    if (result.state === 'denied') return 'denied';
    return 'prompt';
  } catch {
    return 'prompt';
  }
}

async function checkNotifications(): Promise<'granted' | 'denied' | 'prompt'> {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const result = await PushNotifications.checkPermissions();
    if (result.receive === 'granted') return 'granted';
    if (result.receive === 'denied') return 'denied';
    return 'prompt';
  } catch {
    return 'prompt'; // Plugin not available — skip
  }
}

async function requestNotifications(): Promise<'granted' | 'denied'> {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const result = await PushNotifications.requestPermissions();
    return result.receive === 'granted' ? 'granted' : 'denied';
  } catch {
    return 'denied';
  }
}

export const NativePermissionsGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [perms, setPerms] = useState<PermissionState>(INITIAL);
  const [deniedCount, setDeniedCount] = useState(0);

  const checkAll = useCallback(async () => {
    console.log('[PermGate] checkAll() start');
    const mic = await checkMicrophone();
    const notif = await checkNotifications();
    console.log('[PermGate] checkAll() results — mic:', mic, 'notif:', notif);
    setPerms((prev) => ({
      // Only downgrade to 'denied' when the OS explicitly says denied.
      // 'prompt' means the user hasn't decided yet — show the request button.
      // Crucially: never overwrite a 'granted' state with 'denied' just
      // because the Permissions API still returns 'prompt' (Android WebView bug).
      microphone: mic === 'granted' ? 'granted'
        : prev.microphone === 'granted' ? 'granted'
        : 'denied',
      notifications: notif === 'granted' ? 'granted'
        : prev.notifications === 'granted' ? 'granted'
        : 'denied',
    }));
  }, []);

  // Start listening for FCM token early so it's cached before auth
  useEffect(() => {
    console.log('[PermGate] mount, isNative:', isNative());
    if (!isNative()) return;
    cacheNativeToken();
  }, []);

  // Check on mount
  useEffect(() => {
    if (!isNative()) return;
    checkAll();
  }, [checkAll]);

  // Re-check when app resumes from background (user might have changed settings)
  useEffect(() => {
    if (!isNative()) return;
    let active = true;

    const setup = async () => {
      const { App } = await import('@capacitor/app');
      const listener = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive && active) {
          checkAll();
        }
      });
      return () => {
        active = false;
        listener.remove();
      };
    };

    let cleanup: (() => void) | undefined;
    setup().then((fn) => { cleanup = fn; });
    return () => {
      active = false;
      cleanup?.();
    };
  }, [checkAll]);

  // Not native — skip gate entirely
  if (!isNative()) return <>{children}</>;

  // iOS: skip the blocking gate (Apple guideline 4.5.4 — permissions must be optional).
  // Permissions are requested later via the PromptQueueOverlay and settings.
  if (isIos()) return <>{children}</>;

  // Still checking
  if (perms.microphone === 'checking' || perms.notifications === 'checking') {
    return null; // Splash screen is still visible
  }

  const allGranted = perms.microphone === 'granted' && perms.notifications === 'granted';

  if (allGranted) return <>{children}</>;

  const requestAll = async () => {
    setPerms((p) => ({
      microphone: p.microphone === 'denied' ? 'requesting' : p.microphone,
      notifications: p.notifications === 'denied' ? 'requesting' : p.notifications,
    }));

    // Request microphone first (most important) — only getUserMedia when user taps
    if (perms.microphone !== 'granted') {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        micGrantedViaStream = true;
        setPerms((p) => ({ ...p, microphone: 'granted' }));
      } catch {
        setPerms((p) => ({ ...p, microphone: 'denied' }));
      }
    }

    // Then notifications
    if (perms.notifications !== 'granted') {
      const notif = await requestNotifications();
      setPerms((p) => ({ ...p, notifications: notif }));
    }

    // Re-check everything
    await checkAll();
    setDeniedCount((c) => c + 1);
  };

  const showSettingsButton = deniedCount >= 1;

  return (
    <Overlay>
      <Card>
        <Title>Permissions required</Title>
        <Subtitle>
          Mailander needs access to your microphone and notifications to work properly.
        </Subtitle>

        <PermissionRow>
          <PermissionIcon>{perms.microphone === 'granted' ? '✓' : '○'}</PermissionIcon>
          <PermissionLabel>
            Microphone
            <PermissionDesc>Required for voice messages</PermissionDesc>
          </PermissionLabel>
          <StatusBadge $granted={perms.microphone === 'granted'}>
            {perms.microphone === 'granted' ? 'Granted' : 'Required'}
          </StatusBadge>
        </PermissionRow>

        <PermissionRow>
          <PermissionIcon>{perms.notifications === 'granted' ? '✓' : '○'}</PermissionIcon>
          <PermissionLabel>
            Notifications
            <PermissionDesc>Stay updated with new messages</PermissionDesc>
          </PermissionLabel>
          <StatusBadge $granted={perms.notifications === 'granted'}>
            {perms.notifications === 'granted' ? 'Granted' : 'Required'}
          </StatusBadge>
        </PermissionRow>

        <ButtonsCol>
          <PrimaryButton onClick={requestAll}>
            Enable permissions
          </PrimaryButton>

          {showSettingsButton && (
            <SettingsButton onClick={openAppSettings}>
              Open Settings
            </SettingsButton>
          )}
        </ButtonsCol>
      </Card>
    </Overlay>
  );
};

// ─── styled ───

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: #1a1c20;
  border: 1px solid #2b2d33;
  border-radius: 20px;
  padding: 28px 24px;
`;

const Title = styled.h2`
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  color: #e9ecf5;
`;

const Subtitle = styled.p`
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  color: #8c92a3;
`;

const PermissionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #2b2d33;
`;

const PermissionIcon = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #8c92a3;
  flex-shrink: 0;
`;

const PermissionLabel = styled.div`
  flex: 1;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 15px;
  color: #e9ecf5;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PermissionDesc = styled.span`
  font-weight: 400;
  font-size: 12px;
  color: #6b7280;
`;

const StatusBadge = styled.span<{ $granted: boolean }>`
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  flex-shrink: 0;
  background: ${(p) => (p.$granted ? 'rgba(34, 197, 94, 0.12)' : 'rgba(239, 68, 68, 0.12)')};
  color: ${(p) => (p.$granted ? '#22c55e' : '#ef4444')};
`;

const ButtonsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  margin-top: 4px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  height: 48px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  background: #5e84ed;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 15px;
  color: #ffffff;
`;

const SettingsButton = styled.button`
  background: transparent;
  border: 1px solid #3b3d44;
  border-radius: 12px;
  width: 100%;
  height: 44px;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #b7bdcb;
`;
